// src/pages/matching-intergenerationnel/MerciHeirPage.tsx
import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";

type HeirProfile = {
  id?: string;
  name?: string;
  axes?: {
    ouverture: number;
    esthetique: number;
    experience: number;
    collectif: number;
    rapport_au_temps?: "passe" | "present" | "futur";
    keywords?: string[];
  };
};

export default function MerciHeirPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const heirProfile = (location.state?.heirProfile || null) as HeirProfile | null;
  const [copied, setCopied] = useState(false);

  const goToScoring = () => {
    if (heirProfile) {
      navigate("/matching/scoring", { state: { heirProfile } });
    } else {
      // Fallback: si pas de state, on va quand même sur la page scoring
      navigate("/matching/scoring");
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.origin + "/matching";
    const title = "Questionnaire artistique terminé";
    const text = "Je viens de compléter mon questionnaire artistique. Découvre la plateforme !";

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-16 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl rounded-2xl bg-white shadow-xl p-8"
      >
        <div className="flex items-center justify-center mb-6">
          <motion.div
            initial={{ scale: 0.9, rotate: -6, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center"
          >
            <span className="text-3xl">✅</span>
          </motion.div>
        </div>

        <h1 className="text-3xl font-bold text-center text-gray-900">
          Merci {heirProfile?.name ? `, ${heirProfile.name}` : ""} !
        </h1>
        <p className="mt-3 text-center text-gray-600">
          Ton questionnaire de sensibilité artistique a bien été enregistré.
          {heirProfile?.axes && " Nous avons déjà calculé ton profil et préparé des recommandations."}
        </p>

        {heirProfile?.axes && (
          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <Badge label="Ouverture" value={heirProfile.axes.ouverture} />
            <Badge label="Esthétique" value={heirProfile.axes.esthetique} />
            <Badge label="Expérience" value={heirProfile.axes.experience} />
            <Badge label="Collectif" value={heirProfile.axes.collectif} />
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={goToScoring}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            Voir mes recommandations
          </button>

          <Link
            to="/matching"
            className="px-5 py-2.5 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200 transition"
          >
            Retour à l’accueil
          </Link>

          <button
            onClick={handleShare}
            className="px-5 py-2.5 rounded-lg bg-purple-100 text-purple-800 hover:bg-purple-200 transition"
            title="Partager le lien"
          >
            {copied ? "Lien copié ✅" : "Partager"}
          </button>
        </div>

        <p className="mt-6 text-xs text-center text-gray-400">
          Tu pourras revenir à tout moment pour compléter ou ajuster tes réponses.
        </p>
      </motion.div>
    </div>
  );
}

function Badge({ label, value }: { label: string; value?: number }) {
  const v = typeof value === "number" ? `${value}/100` : "—";
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-2">
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold text-gray-900">{v}</span>
    </div>
  );
}
